import { FIXED_TICK_HZ } from "../../core/timing.ts";
import type { ArrivalProfile, ReadonlyPointXZ } from "./types.ts";

const SHA256_INITIAL = [
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
] as const;
const SHA256_CONSTANTS = [
  0x428a2f98,0x71374491,0xb5c0fbcf,0xe9b5dba5,0x3956c25b,0x59f111f1,0x923f82a4,0xab1c5ed5,
  0xd807aa98,0x12835b01,0x243185be,0x550c7dc3,0x72be5d74,0x80deb1fe,0x9bdc06a7,0xc19bf174,
  0xe49b69c1,0xefbe4786,0x0fc19dc6,0x240ca1cc,0x2de92c6f,0x4a7484aa,0x5cb0a9dc,0x76f988da,
  0x983e5152,0xa831c66d,0xb00327c8,0xbf597fc7,0xc6e00bf3,0xd5a79147,0x06ca6351,0x14292967,
  0x27b70a85,0x2e1b2138,0x4d2c6dfc,0x53380d13,0x650a7354,0x766a0abb,0x81c2c92e,0x92722c85,
  0xa2bfe8a1,0xa81a664b,0xc24b8b70,0xc76c51a3,0xd192e819,0xd6990624,0xf40e3585,0x106aa070,
  0x19a4c116,0x1e376c08,0x2748774c,0x34b0bcb5,0x391c0cb3,0x4ed8aa4a,0x5b9cca4f,0x682e6ff3,
  0x748f82ee,0x78a5636f,0x84c87814,0x8cc70208,0x90befffa,0xa4506ceb,0xbef9a3f7,0xc67178f2,
] as const;

function rotateRight(value: number, bits: number): number {
  return (value >>> bits) | (value << (32 - bits));
}

export function sha256Hex(text: string): string {
  const input = new TextEncoder().encode(text);
  const paddedLength = Math.ceil((input.length + 9) / 64) * 64;
  const bytes = new Uint8Array(paddedLength);
  bytes.set(input);
  bytes[input.length] = 0x80;
  const bitLength = BigInt(input.length) * 8n;
  for (let index = 0; index < 8; index += 1) bytes[paddedLength - 1 - index] = Number((bitLength >> BigInt(index * 8)) & 0xffn);
  const hash: number[] = [...SHA256_INITIAL];
  const words = new Uint32Array(64);
  for (let offset = 0; offset < bytes.length; offset += 64) {
    for (let index = 0; index < 16; index += 1) {
      const byte = offset + index * 4;
      words[index] = ((bytes[byte]! << 24) | (bytes[byte + 1]! << 16) | (bytes[byte + 2]! << 8) | bytes[byte + 3]!) >>> 0;
    }
    for (let index = 16; index < 64; index += 1) {
      const x = words[index - 15]!;
      const y = words[index - 2]!;
      const sigma0 = rotateRight(x, 7) ^ rotateRight(x, 18) ^ (x >>> 3);
      const sigma1 = rotateRight(y, 17) ^ rotateRight(y, 19) ^ (y >>> 10);
      words[index] = (words[index - 16]! + sigma0 + words[index - 7]! + sigma1) >>> 0;
    }
    let [a, b, c, d, e, f, g, h] = hash as number[];
    for (let index = 0; index < 64; index += 1) {
      const sum1 = rotateRight(e!, 6) ^ rotateRight(e!, 11) ^ rotateRight(e!, 25);
      const choose = (e! & f!) ^ (~e! & g!);
      const first = (h! + sum1 + choose + SHA256_CONSTANTS[index]! + words[index]!) >>> 0;
      const sum0 = rotateRight(a!, 2) ^ rotateRight(a!, 13) ^ rotateRight(a!, 22);
      const majority = (a! & b!) ^ (a! & c!) ^ (b! & c!);
      const second = (sum0 + majority) >>> 0;
      h = g; g = f; f = e; e = (d! + first) >>> 0; d = c; c = b; b = a; a = (first + second) >>> 0;
    }
    const next = [a!, b!, c!, d!, e!, f!, g!, h!];
    for (let index = 0; index < 8; index += 1) hash[index] = (hash[index]! + next[index]!) >>> 0;
  }
  return hash.map((word) => word.toString(16).padStart(8, "0")).join("");
}

export function computeArrivalTicks(costMilliWU: number, profile: Readonly<ArrivalProfile>): number {
  const length = BigInt(costMilliWU);
  const speed = BigInt(profile.speedMilliWUPerSecond);
  const acceleration = BigInt(profile.accelerationMilliWUPerSecondSquared);
  const deceleration = BigInt(profile.decelerationMilliWUPerSecondSquared);
  const denominator = 2n * speed * acceleration * deceleration;
  const travelNumerator = 2n * length * acceleration * deceleration + speed * speed * deceleration + speed * speed * acceleration;
  const tickNumerator = BigInt(FIXED_TICK_HZ) * travelNumerator;
  return Number((tickNumerator + denominator - 1n) / denominator);
}

export interface FingerprintFrame {
  readonly revision: number;
  readonly fromNodeId: string;
  readonly toNodeId: string;
  readonly totalCostMilliWU: number;
  readonly profileId: string;
  readonly arrivalTicks: number;
  readonly waypoints: readonly ReadonlyPointXZ[];
}

export function frameRouteFingerprint(route: Readonly<FingerprintFrame>): string {
  const points = route.waypoints.map((point) => `${point[0]},${point[1]}`).join(";");
  return `NTNAV1|r=${route.revision}|from=${route.fromNodeId}|to=${route.toNodeId}|cost=${route.totalCostMilliWU}|profile=${route.profileId}|ticks=${route.arrivalTicks}|pts=${points}`;
}
